import React from 'react';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { PurchaseTermsAcceptance } from 'Views/Components/_HumanWritten/Payment/PurchaseTermsAcceptance';
import { useAnytimeTicketStore, useAnytimeTicketStoreApi } from '../State/AnytimeTicketContext';
import { useProductList } from 'Modules/Products/Hooks/useProductList';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { useInitiateAnytimeTransaction } from '../Mutations/useInitiateAnytimeTransaction';
// Footer receives scrollModalToTop as prop name from the modal
interface TermsFooterProps {
	onCancel: () => void;
	onDone: () => void;
	scrollModalToTop: () => void;
}

export default function AnytimeTicketTermsStep() {
	const selectedProductId = useAnytimeTicketStore(s => s.selectedProductId);
	const termsAccepted = useAnytimeTicketStore(s => s.termsAccepted);
	const acceptTerms = useAnytimeTicketStore(s => s.acceptTerms);
	const termsPromptCount = useAnytimeTicketStore(s => s.termsPromptCount);

	const { data } = useProductList();
	const product = data?.items.find(p => p.id === selectedProductId);

	const terms = product?.termsAndConditions || whiteLabelStore.config?.defaultProductTerms || '';

	return (
		<PurchaseTermsAcceptance
			title="Terms and Conditions"
			termsHtml={terms}
			accepted={termsAccepted}
			onAcceptedChange={acceptTerms}
			promptCount={termsPromptCount}
			testId="anytime-ticket-terms-accept"
		/>
	);
}

AnytimeTicketTermsStep.Footer = function AnytimeTicketTermsFooter({ scrollModalToTop }: TermsFooterProps) {
	const termsAccepted = useAnytimeTicketStore(s => s.termsAccepted);
	const selectedProductId = useAnytimeTicketStore(s => s.selectedProductId);
	const selectedProductType = useAnytimeTicketStore(s => s.selectedProductType);
	const selectedCustomerId = useAnytimeTicketStore(s => s.selectedCustomerId);
	const productQuantity = useAnytimeTicketStore(s => s.productQuantity);
	const startCheckout = useAnytimeTicketStore(s => s.startCheckout);
	const promptForTerms = useAnytimeTicketStore(s => s.promptForTerms);
	const storeApi = useAnytimeTicketStoreApi();

	const { mutate: initiateTransaction, isPending } = useInitiateAnytimeTransaction({
		onSuccess: data => {
			startCheckout(data.transactionId, data.userEntitlementId, data.purchasedItemId);
		},
	});

	return (
		<NewModal.Footer>
			<Button
				display={Display.Outline}
				colors={Colors.Grey}
				newStyles
				onClick={() => {
					storeApi.getState().acceptTerms(false);
					storeApi.setState({ step: 'select_product', termsPromptCount: 0 });
					scrollModalToTop();
				}}
			>
				Back
			</Button>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				newStyles
				// Left clickable while the terms are unticked so the press can raise a validation error
				// against the checkbox, matching the product step's Select customer field. A disabled
				// button gives the customer nothing to act on.
				disabled={isPending}
				onClick={() => {
					if (!termsAccepted) {
						promptForTerms();
						return;
					}
					if (selectedProductId) {
						scrollModalToTop();
						initiateTransaction({
							productId: selectedProductId,
							productType: selectedProductType || 'TICKET',
							customerId: selectedCustomerId,
							quantity: productQuantity,
						});
					}
				}}
			>
				{isPending ? 'Processing...' : 'Continue to payment'}
			</Button>
		</NewModal.Footer>
	);
};
